import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import BusinessIcon from '@mui/icons-material/Business'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import DescriptionIcon from '@mui/icons-material/Description'
import EditIcon from '@mui/icons-material/Edit'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import PersonIcon from '@mui/icons-material/Person'
import PrintIcon from '@mui/icons-material/Print'
import ScheduleIcon from '@mui/icons-material/Schedule'
import SendIcon from '@mui/icons-material/Send'

import { useGetActsQuery, useLazyGetActsQuery } from '@/core/services/acts'
import { ActCondition, ActConditionType, WaitingRoomActConfig } from '@/core/models/network.model'
import { Act } from '@/core/models/acts.model'
import { StaffType } from '@/utils/onboarding/type'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface EstablishmentOption {
  _id: string
  name: string
}

export interface WaitingRoomOption {
  _id: string
  name: string
}

/** Subset of Act shown in wizard UI — matches what the Autocomplete and review step render. */
type ActOption = Pick<Act, '_id' | 'libelle' | 'honoraire' | 'id_transposition'>


// ─────────────────────────────────────────────────────────────────────────────
// Step components
// ─────────────────────────────────────────────────────────────────────────────

function StepEstablishment({
  establishments,
  selected,
  onSelect,
}: {
  establishments: EstablishmentOption[]
  selected: string
  onSelect: (id: string) => void
}) {
  const { t } = useTranslation()

  return (
    <Stack spacing={2}>
      <Typography variant="body1" color="text.secondary">
        {t('page.network.actWizard.step.establishment.description', {
          defaultValue: 'Which establishment should this billing act be linked to? Each establishment has its own Desmos configuration and act catalog.',
        })}
      </Typography>

      {establishments.length === 0 ? (
        <Alert severity="warning">
          {t('page.network.actWizard.step.establishment.noEstablishments', {
            defaultValue: 'No establishments found in this network. Make sure network members have an establishment configured.',
          })}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {establishments.map((est) => (
            <Grid item xs={12} sm={6} md={4} key={est._id}>
              <Card
                variant={selected === est._id ? 'elevation' : 'outlined'}
                sx={{
                  borderColor: selected === est._id ? 'primary.main' : undefined,
                  borderWidth: selected === est._id ? 2 : 1,
                  bgcolor: selected === est._id ? 'primary.50' : undefined,
                }}
              >
                <CardActionArea onClick={() => onSelect(est._id)} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <BusinessIcon color={selected === est._id ? 'primary' : 'action'} />
                    <Typography variant="subtitle1" fontWeight={selected === est._id ? 600 : 400}>
                      {est.name}
                    </Typography>
                    {selected === est._id && <CheckCircleIcon color="primary" fontSize="small" sx={{ ml: 'auto' }} />}
                  </Stack>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  )
}

function StepRoleAndAct({
  role,
  onRoleChange,
  selectedAct,
  onActChange,
  establishmentId,
}: {
  role: StaffType
  onRoleChange: (role: StaffType) => void
  selectedAct: Act | null
  onActChange: (act: Act | null) => void
  establishmentId: string
}) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')

  const { data: acts = [], isLoading: isLoadingActs } = useGetActsQuery(
    establishmentId ? { establishment: establishmentId } : {}
  )
  const [getActs, { isLoading: isSearching }] = useLazyGetActsQuery()

  const handleInputChange = (_e: React.SyntheticEvent, newInput: string) => {
    setInputValue(newInput)
    if (newInput.length > 0) {
      getActs({ smartSearch: newInput, establishment: establishmentId })
    }
  }

  return (
    <Stack spacing={3}>
      {/* Role selection */}
      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          {t('page.network.actWizard.step.roleAct.roleTitle', {
            defaultValue: 'Who performs this act?',
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('page.network.actWizard.step.roleAct.roleDescription', {
            defaultValue: 'This determines whose professional card (CPS) is used when submitting the act to the billing software.',
          })}
        </Typography>
        <Grid container spacing={2}>
          {([StaffType.MEDECIN, StaffType.PDS] as const).map((r) => (
            <Grid item xs={6} key={r}>
              <Card
                variant={role === r ? 'elevation' : 'outlined'}
                sx={{
                  borderColor: role === r ? 'primary.main' : undefined,
                  borderWidth: role === r ? 2 : 1,
                  bgcolor: role === r ? 'primary.50' : undefined,
                }}
              >
                <CardActionArea onClick={() => onRoleChange(r)} sx={{ p: 2 }}>
                  <Stack alignItems="center" spacing={1}>
                    {r === StaffType.MEDECIN ? (
                      <MedicalServicesIcon color={role === r ? 'primary' : 'action'} fontSize="large" />
                    ) : (
                      <PersonIcon color={role === r ? 'primary' : 'action'} fontSize="large" />
                    )}
                    <Typography variant="subtitle1" fontWeight={role === r ? 600 : 400}>
                      {r === StaffType.MEDECIN
                        ? t('page.network.actWizard.role.medecin', { defaultValue: 'Doctor' })
                        : t('page.network.actWizard.role.pds', { defaultValue: 'Nurse (PDS)' })}
                    </Typography>
                    {role === r && <CheckCircleIcon color="primary" fontSize="small" />}
                  </Stack>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Divider />

      {/* Act selection */}
      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          {t('page.network.actWizard.step.roleAct.actTitle', {
            defaultValue: 'Which act?',
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('page.network.actWizard.step.roleAct.actDescription', {
            defaultValue: 'Search for the medical act by name or code. These acts come from your establishment\'s catalog.',
          })}
        </Typography>
        <Autocomplete
          value={selectedAct}
          onChange={(_e, newValue) => onActChange(newValue)}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          options={acts}
          getOptionLabel={(o: Act) => `${o.libelle} (${o.id_transposition}) — ${o.honoraire}€`}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          loading={isLoadingActs || isSearching}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('page.network.actWizard.step.roleAct.actSearch', { defaultValue: 'Search for an act...' })}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {(isLoadingActs || isSearching) && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, o: Act) => (
            <li {...props} key={o._id}>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ width: '100%' }}>
                <Typography variant="body1" fontWeight={500}>{o.libelle}</Typography>
                <Typography variant="body2" color="text.secondary">{o.id_transposition}</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ ml: 'auto !important' }}>{o.honoraire}€</Typography>
              </Stack>
            </li>
          )}
        />

        {selectedAct && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>{selectedAct.libelle}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('page.network.actWizard.step.roleAct.actCode', { defaultValue: 'Code' })}: {selectedAct.id_transposition}
                </Typography>
              </Box>
              <Typography variant="h6" color="primary.main">{selectedAct.honoraire}€</Typography>
            </Stack>
          </Paper>
        )}
      </Box>
    </Stack>
  )
}

function StepAutomation({
  autoCreateDesmos,
  autoCreateFSP,
  onDesmosChange,
  onFSPChange,
}: {
  autoCreateDesmos: boolean
  autoCreateFSP: boolean
  onDesmosChange: (v: boolean) => void
  onFSPChange: (v: boolean) => void
}) {
  const { t } = useTranslation()

  return (
    <Stack spacing={3}>
      <Typography variant="body1" color="text.secondary">
        {t('page.network.actWizard.step.automation.description', {
          defaultValue: 'Choose what happens automatically when a consultation ends in this waiting room.',
        })}
      </Typography>

      <Grid container spacing={2}>
        {/* Desmos toggle */}
        <Grid item xs={12} sm={6}>
          <Card
            variant={autoCreateDesmos ? 'elevation' : 'outlined'}
            sx={{
              height: '100%',
              borderColor: autoCreateDesmos ? 'success.main' : undefined,
              borderWidth: autoCreateDesmos ? 2 : 1,
              bgcolor: autoCreateDesmos ? 'success.50' : undefined,
              cursor: 'pointer',
            }}
            onClick={() => onDesmosChange(!autoCreateDesmos)}
          >
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <SendIcon color={autoCreateDesmos ? 'success' : 'action'} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('page.network.actWizard.step.automation.desmos.title', {
                      defaultValue: 'Send to Desmos',
                    })}
                  </Typography>
                  <Checkbox
                    checked={autoCreateDesmos}
                    sx={{ ml: 'auto', p: 0 }}
                    color="success"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onDesmosChange(e.target.checked)}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t('page.network.actWizard.step.automation.desmos.description', {
                    defaultValue: 'Automatically transmit this act to the Desmos billing software when the consultation ends. The act will appear in Desmos for final validation and billing.',
                  })}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* FSP toggle */}
        <Grid item xs={12} sm={6}>
          <Card
            variant={autoCreateFSP ? 'elevation' : 'outlined'}
            sx={{
              height: '100%',
              borderColor: autoCreateFSP ? 'success.main' : undefined,
              borderWidth: autoCreateFSP ? 2 : 1,
              bgcolor: autoCreateFSP ? 'success.50' : undefined,
              cursor: 'pointer',
            }}
            onClick={() => onFSPChange(!autoCreateFSP)}
          >
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PrintIcon color={autoCreateFSP ? 'success' : 'action'} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('page.network.actWizard.step.automation.fsp.title', {
                      defaultValue: 'Generate paper form (FSP)',
                    })}
                  </Typography>
                  <Checkbox
                    checked={autoCreateFSP}
                    sx={{ ml: 'auto', p: 0 }}
                    color="success"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onFSPChange(e.target.checked)}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t('page.network.actWizard.step.automation.fsp.description', {
                    defaultValue: 'Automatically generate a "Feuille de Soins Papier" (FSP) PDF that can be printed and signed. Useful when electronic billing is unavailable or for specific patient situations.',
                  })}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  )
}

function StepScope({
  waitingRooms,
  selectedWaitingRooms,
  onWaitingRoomsChange,
}: {
  waitingRooms: WaitingRoomOption[]
  selectedWaitingRooms: string[]
  onWaitingRoomsChange: (ids: string[]) => void
}) {
  const { t } = useTranslation()
  const allSelected = selectedWaitingRooms.length === 0

  return (
    <Stack spacing={2}>
      <Typography variant="body1" color="text.secondary">
        {t('page.network.actWizard.step.scope.description', {
          defaultValue: 'By default, this act applies to all waiting rooms. You can restrict it to specific ones if needed.',
        })}
      </Typography>

      <Card
        variant={allSelected ? 'elevation' : 'outlined'}
        sx={{
          borderColor: allSelected ? 'primary.main' : undefined,
          borderWidth: allSelected ? 2 : 1,
          bgcolor: allSelected ? 'primary.50' : undefined,
          cursor: 'pointer',
        }}
        onClick={() => onWaitingRoomsChange([])}
      >
        <CardActionArea sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon color={allSelected ? 'primary' : 'disabled'} />
            <Box>
              <Typography variant="subtitle1" fontWeight={allSelected ? 600 : 400}>
                {t('page.network.actWizard.step.scope.allRooms', { defaultValue: 'All waiting rooms' })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('page.network.actWizard.step.scope.allRoomsHint', {
                  defaultValue: 'This act will apply to every waiting room in the network, including future ones.',
                })}
              </Typography>
            </Box>
          </Stack>
        </CardActionArea>
      </Card>

      <Divider>
        <Typography variant="caption" color="text.secondary">
          {t('page.network.actWizard.step.scope.or', { defaultValue: 'or select specific rooms' })}
        </Typography>
      </Divider>

      {waitingRooms.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('page.network.actWizard.step.scope.noRooms', {
            defaultValue: 'No waiting rooms found in this network.',
          })}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {waitingRooms.map((room) => {
            const isSelected = selectedWaitingRooms.includes(room._id)
            return (
              <Card
                key={room._id}
                variant="outlined"
                sx={{
                  borderColor: isSelected ? 'primary.main' : undefined,
                  cursor: 'pointer',
                }}
              >
                <CardActionArea
                  sx={{ p: 1.5 }}
                  onClick={() => {
                    if (isSelected) {
                      const next = selectedWaitingRooms.filter((id) => id !== room._id)
                      onWaitingRoomsChange(next)
                    } else {
                      onWaitingRoomsChange([...selectedWaitingRooms, room._id])
                    }
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox checked={isSelected} sx={{ pointerEvents: 'none', p: 0 }} />
                    <Typography variant="body1">{room.name}</Typography>
                  </Stack>
                </CardActionArea>
              </Card>
            )
          })}
        </Stack>
      )}
    </Stack>
  )
}

function StepConditions({
  conditions,
  onConditionsChange,
}: {
  conditions: ActCondition[]
  onConditionsChange: (conditions: ActCondition[]) => void
}) {
  const { t } = useTranslation()
  const hasAlways = conditions.length === 0 || conditions.some((c) => c.type === 'always')
  const hasTime = conditions.some((c) => c.type === 'time')
  const hasAge = conditions.some((c) => c.type === 'patient_age')

  const timeCondition = conditions.find((c) => c.type === 'time')
  const ageCondition = conditions.find((c) => c.type === 'patient_age')

  const setMode = (mode: 'always' | 'time' | 'patient_age') => {
    if (mode === 'always') {
      onConditionsChange([])
    } else if (mode === 'time') {
      onConditionsChange([{ type: 'time', after: '', before: '' }])
    } else {
      onConditionsChange([{ type: 'patient_age' }])
    }
  }

  const updateTimeCondition = (field: 'after' | 'before', value: string) => {
    const updated = conditions.map((c) =>
      c.type === 'time' ? { ...c, [field]: value } : c
    )
    onConditionsChange(updated)
  }

  const updateAgeCondition = (field: 'minAge' | 'maxAge', value: string) => {
    const updated = conditions.map((c) =>
      c.type === 'patient_age'
        ? { ...c, [field]: value === '' ? undefined : parseInt(value, 10) }
        : c
    )
    onConditionsChange(updated)
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body1" color="text.secondary">
        {t('page.network.actWizard.step.conditions.description', {
          defaultValue: 'Should this act only apply in certain situations? Most of the time, "Always" is the right choice.',
        })}
      </Typography>

      <Stack spacing={2}>
        {/* Always */}
        <Card
          variant={hasAlways && !hasTime && !hasAge ? 'elevation' : 'outlined'}
          sx={{
            borderColor: hasAlways && !hasTime && !hasAge ? 'primary.main' : undefined,
            borderWidth: hasAlways && !hasTime && !hasAge ? 2 : 1,
            bgcolor: hasAlways && !hasTime && !hasAge ? 'primary.50' : undefined,
            cursor: 'pointer',
          }}
          onClick={() => setMode('always')}
        >
          <CardActionArea sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon color={hasAlways && !hasTime && !hasAge ? 'primary' : 'disabled'} />
              <Box>
                <Typography variant="subtitle1" fontWeight={hasAlways && !hasTime && !hasAge ? 600 : 400}>
                  {t('page.network.actWizard.step.conditions.always', { defaultValue: 'Always' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('page.network.actWizard.step.conditions.alwaysHint', {
                    defaultValue: 'This act applies to every consultation, regardless of time or patient.',
                  })}
                </Typography>
              </Box>
            </Stack>
          </CardActionArea>
        </Card>

        {/* Time range */}
        <Card
          variant={hasTime ? 'elevation' : 'outlined'}
          sx={{
            borderColor: hasTime ? 'primary.main' : undefined,
            borderWidth: hasTime ? 2 : 1,
            bgcolor: hasTime ? 'primary.50' : undefined,
            cursor: 'pointer',
          }}
          onClick={() => { if (!hasTime) setMode('time') }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: hasTime ? 2 : 0 }}>
              <ScheduleIcon color={hasTime ? 'primary' : 'disabled'} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={hasTime ? 600 : 400}>
                  {t('page.network.actWizard.step.conditions.timeRange', { defaultValue: 'During specific hours' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('page.network.actWizard.step.conditions.timeRangeHint', {
                    defaultValue: 'Only apply this act for consultations during a specific time window (e.g., night shifts, weekends).',
                  })}
                </Typography>
              </Box>
            </Stack>
            {hasTime && (
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <TextField
                  type="time"
                  size="small"
                  label={t('page.network.actWizard.step.conditions.after', { defaultValue: 'After' })}
                  value={timeCondition?.after ?? ''}
                  onChange={(e) => updateTimeCondition('after', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  type="time"
                  size="small"
                  label={t('page.network.actWizard.step.conditions.before', { defaultValue: 'Before' })}
                  value={timeCondition?.before ?? ''}
                  onChange={(e) => updateTimeCondition('before', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* Patient age */}
        <Card
          variant={hasAge ? 'elevation' : 'outlined'}
          sx={{
            borderColor: hasAge ? 'primary.main' : undefined,
            borderWidth: hasAge ? 2 : 1,
            bgcolor: hasAge ? 'primary.50' : undefined,
            cursor: 'pointer',
          }}
          onClick={() => { if (!hasAge) setMode('patient_age') }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: hasAge ? 2 : 0 }}>
              <PersonIcon color={hasAge ? 'primary' : 'disabled'} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={hasAge ? 600 : 400}>
                  {t('page.network.actWizard.step.conditions.patientAge', { defaultValue: 'Based on patient age' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('page.network.actWizard.step.conditions.patientAgeHint', {
                    defaultValue: 'Only apply this act when the patient falls within a specific age range.',
                  })}
                </Typography>
              </Box>
            </Stack>
            {hasAge && (
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <TextField
                  type="number"
                  size="small"
                  label={t('page.network.actWizard.step.conditions.minAge', { defaultValue: 'Minimum age (years)' })}
                  value={ageCondition?.minAge ?? ''}
                  onChange={(e) => updateAgeCondition('minAge', e.target.value)}
                  InputProps={{ inputProps: { min: 0 } }}
                  fullWidth
                />
                <TextField
                  type="number"
                  size="small"
                  label={t('page.network.actWizard.step.conditions.maxAge', { defaultValue: 'Maximum age (years)' })}
                  value={ageCondition?.maxAge ?? ''}
                  onChange={(e) => updateAgeCondition('maxAge', e.target.value)}
                  InputProps={{ inputProps: { min: 0 } }}
                  fullWidth
                />
              </Stack>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Review step — readable summary
// ─────────────────────────────────────────────────────────────────────────────

function StepReview({
  formData,
  establishments,
  waitingRooms,
  actDetails,
}: {
  formData: WaitingRoomActConfig
  establishments: EstablishmentOption[]
  waitingRooms: WaitingRoomOption[]
  actDetails?: ActOption | null
}) {
  const { t } = useTranslation()

  const estName = establishments.find((e) => e._id === formData.establishmentId)?.name ?? '—'
  const roleName = formData.role === StaffType.MEDECIN
    ? t('page.network.actWizard.role.medecin', { defaultValue: 'Doctor' })
    : t('page.network.actWizard.role.pds', { defaultValue: 'Nurse (PDS)' })
  const actName = actDetails?.libelle ?? '—'
  const actCode = actDetails?.id_transposition ?? ''
  const actFee = actDetails?.honoraire ?? ''

  const scopeLabel = formData.waitingRooms.length === 0
    ? t('page.network.actWizard.review.allRooms', { defaultValue: 'all waiting rooms' })
    : formData.waitingRooms
        .map((id) => waitingRooms.find((wr) => wr._id === id)?.name ?? id)
        .join(', ')

  const conditionLabel = (() => {
    if (formData.conditions.length === 0) {
      return t('page.network.actWizard.review.always', { defaultValue: 'at any time' })
    }
    return formData.conditions.map((c) => {
      if (c.type === 'time') {
        const after = c.after || '00:00'
        const before = c.before || '23:59'
        return t('page.network.actWizard.review.timeRange', {
          defaultValue: 'between {{after}} and {{before}}',
          after,
          before,
        })
      }
      if (c.type === 'patient_age') {
        if (c.minAge !== undefined && c.maxAge !== undefined) {
          return t('page.network.actWizard.review.ageRange', {
            defaultValue: 'for patients aged {{min}} to {{max}}',
            min: c.minAge,
            max: c.maxAge,
          })
        }
        if (c.minAge !== undefined) {
          return t('page.network.actWizard.review.ageMin', {
            defaultValue: 'for patients aged {{min}} or older',
            min: c.minAge,
          })
        }
        if (c.maxAge !== undefined) {
          return t('page.network.actWizard.review.ageMax', {
            defaultValue: 'for patients aged {{max}} or younger',
            max: c.maxAge,
          })
        }
        return t('page.network.actWizard.review.always', { defaultValue: 'at any time' })
      }
      return ''
    }).filter(Boolean).join(', ')
  })()

  const automationParts: string[] = []
  if (formData.autoCreateDesmos) {
    automationParts.push(t('page.network.actWizard.review.sendDesmos', { defaultValue: 'send to Desmos' }))
  }
  if (formData.autoCreateFSP) {
    automationParts.push(t('page.network.actWizard.review.generateFSP', { defaultValue: 'generate FSP' }))
  }
  if (automationParts.length === 0) {
    automationParts.push(t('page.network.actWizard.review.noAutomation', { defaultValue: 'no automatic action' }))
  }

  return (
    <Stack spacing={3}>
      <Alert severity="info" icon={<DescriptionIcon />}>
        <Typography variant="body1">
          {t('page.network.actWizard.review.summary', {
            defaultValue:
              'For "{{establishment}}", when a {{role}} completes a consultation, automatically {{automation}} for "{{act}}" ({{code}} — {{fee}}€). Applies to {{scope}}, {{conditions}}.',
            establishment: estName,
            role: roleName.toLowerCase(),
            automation: automationParts.join(' + '),
            act: actName,
            code: actCode,
            fee: actFee,
            scope: scopeLabel,
            conditions: conditionLabel,
          })}
        </Typography>
      </Alert>

      <Stack spacing={2}>
        <ReviewRow
          label={t('page.network.actWizard.review.establishmentLabel', { defaultValue: 'Establishment' })}
          value={estName}
          icon={<BusinessIcon fontSize="small" />}
        />
        <ReviewRow
          label={t('page.network.actWizard.review.roleLabel', { defaultValue: 'Practitioner' })}
          value={roleName}
          icon={<PersonIcon fontSize="small" />}
        />
        <ReviewRow
          label={t('page.network.actWizard.review.actLabel', { defaultValue: 'Act' })}
          value={`${actName} (${actCode}) — ${actFee}€`}
          icon={<MedicalServicesIcon fontSize="small" />}
        />
        <ReviewRow
          label={t('page.network.actWizard.review.automationLabel', { defaultValue: 'Automation' })}
          value={
            <Stack direction="row" spacing={1}>
              <Chip
                label={formData.autoCreateDesmos ? 'Desmos ✓' : 'Desmos ✗'}
                size="small"
                color={formData.autoCreateDesmos ? 'success' : 'default'}
                variant={formData.autoCreateDesmos ? 'filled' : 'outlined'}
              />
              <Chip
                label={formData.autoCreateFSP ? 'FSP ✓' : 'FSP ✗'}
                size="small"
                color={formData.autoCreateFSP ? 'success' : 'default'}
                variant={formData.autoCreateFSP ? 'filled' : 'outlined'}
              />
            </Stack>
          }
          icon={<SendIcon fontSize="small" />}
        />
        <ReviewRow
          label={t('page.network.actWizard.review.scopeLabel', { defaultValue: 'Waiting rooms' })}
          value={scopeLabel}
          icon={<ScheduleIcon fontSize="small" />}
        />
        <ReviewRow
          label={t('page.network.actWizard.review.conditionsLabel', { defaultValue: 'Conditions' })}
          value={conditionLabel}
          icon={<ScheduleIcon fontSize="small" />}
        />
      </Stack>
    </Stack>
  )
}

function ReviewRow({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      {icon}
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography variant="body1" fontWeight={500}>{value}</Typography>
      ) : (
        value
      )}
    </Stack>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Summary view for edit mode — click a section to jump to that step
// ─────────────────────────────────────────────────────────────────────────────

export function ActConfigSummaryCard({
  config,
  establishments,
  waitingRooms,
  actDetails,
  onEdit,
  onDelete,
}: {
  config: WaitingRoomActConfig
  establishments: EstablishmentOption[]
  waitingRooms: WaitingRoomOption[]
  actDetails?: ActOption | null
  onEdit: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()

  const estName = establishments.find((e) => e._id === config.establishmentId)?.name ?? config.establishmentId
  const roleName = config.role === StaffType.MEDECIN
    ? t('page.network.actWizard.role.medecin', { defaultValue: 'Doctor' })
    : t('page.network.actWizard.role.pds', { defaultValue: 'Nurse (PDS)' })
  const actName = actDetails?.libelle ?? `Act: ${config.actId}`
  const actCode = actDetails?.id_transposition ?? ''
  const actFee = actDetails?.honoraire ?? ''

  // Defensive defaults: legacy NetworkDesmosAct items can leak through with
  // optional/missing waitingRooms and no conditions field.
  const configWaitingRooms = config.waitingRooms ?? []
  const configConditions = config.conditions ?? []

  const scopeChips = configWaitingRooms.length === 0
    ? [{ label: t('page.network.actWizard.step.scope.allRooms', { defaultValue: 'All waiting rooms' }), id: 'all' }]
    : configWaitingRooms.map((id) => ({
        label: waitingRooms.find((wr) => wr._id === id)?.name ?? id,
        id,
      }))

  const conditionLabel = (() => {
    // No conditions or only 'always' type → "at any time"
    const meaningful = configConditions.filter((c) => c.type !== 'always')
    if (configConditions.length === 0 || meaningful.length === 0) {
      return t('page.network.actWizard.review.always', { defaultValue: 'at any time' })
    }
    return meaningful
      .map((c) => {
        if (c.type === 'time') {
          const after = c.after || '00:00'
          const before = c.before || '23:59'
          return t('page.network.actWizard.review.timeRange', { defaultValue: 'between {{after}} and {{before}}', after, before })
        }
        if (c.type === 'patient_age') {
          if (c.minAge !== undefined && c.maxAge !== undefined) {
            return t('page.network.actWizard.review.ageRange', { defaultValue: 'for patients aged {{min}} to {{max}}', min: c.minAge, max: c.maxAge })
          }
          if (c.minAge !== undefined) {
            return t('page.network.actWizard.review.ageMin', { defaultValue: 'for patients aged {{min}} or older', min: c.minAge })
          }
          if (c.maxAge !== undefined) {
            return t('page.network.actWizard.review.ageMax', { defaultValue: 'for patients aged {{max}} or younger', max: c.maxAge })
          }
        }
        return ''
      })
      .filter(Boolean)
      .join(', ') || t('page.network.actWizard.review.always', { defaultValue: 'at any time' })
  })()

  return (
    <Paper variant="outlined" sx={{ p: 2, '&:hover': { borderColor: 'primary.main' } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flex: 1, cursor: 'pointer' }} onClick={onEdit}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>{actName}</Typography>
            {actCode && (
              <Typography variant="body2" color="text.secondary">({actCode})</Typography>
            )}
            {actFee && (
              <Chip label={`${actFee}€`} size="small" color="primary" variant="outlined" />
            )}
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
            <Chip label={roleName} size="small" color={config.role === StaffType.PDS ? 'info' : 'default'} />
            <Chip
              label={config.autoCreateDesmos ? 'Desmos ✓' : 'Desmos ✗'}
              size="small"
              color={config.autoCreateDesmos ? 'success' : 'default'}
              variant="outlined"
            />
            <Chip
              label={config.autoCreateFSP ? 'FSP ✓' : 'FSP ✗'}
              size="small"
              color={config.autoCreateFSP ? 'success' : 'default'}
              variant="outlined"
            />
            {scopeChips.map((sc) => (
              <Chip key={sc.id} label={sc.label} size="small" color="primary" variant="outlined" />
            ))}
            {conditionLabel !== t('page.network.actWizard.review.always', { defaultValue: 'at any time' }) && (
              <Chip label={conditionLabel} size="small" variant="outlined" />
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
          <IconButton size="small" onClick={onEdit} color="primary">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onDelete} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Wizard dialog
// ─────────────────────────────────────────────────────────────────────────────

const STEP_KEYS = ['establishment', 'roleAct', 'automation', 'scope', 'conditions', 'review'] as const
type StepKey = typeof STEP_KEYS[number]

export interface ActConfigWizardProps {
  open: boolean
  onClose: () => void
  onSave: (config: WaitingRoomActConfig) => void
  initialConfig?: WaitingRoomActConfig | null
  establishments: EstablishmentOption[]
  waitingRooms: WaitingRoomOption[]
  isSaving?: boolean
}

export default function ActConfigWizard({
  open,
  onClose,
  onSave,
  initialConfig,
  establishments,
  waitingRooms,
  isSaving,
}: ActConfigWizardProps) {
  const { t } = useTranslation()
  const isEditMode = !!initialConfig

  // If editing, start at review step; if creating, start at step 0
  const [activeStep, setActiveStep] = useState(0)

  const [formData, setFormData] = useState<WaitingRoomActConfig>({
    actId: '',
    establishmentId: '',
    role: StaffType.MEDECIN,
    autoCreateDesmos: true,
    autoCreateFSP: false,
    conditions: [],
    waitingRooms: [],
  })
  const [selectedAct, setSelectedAct] = useState<Act | null>(null)

  // Resolve act details for edit mode
  const { data: acts = [] } = useGetActsQuery(
    formData.establishmentId ? { establishment: formData.establishmentId } : {},
    { skip: !formData.establishmentId }
  )

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (initialConfig) {
        setFormData({
          ...initialConfig,
          conditions: initialConfig.conditions ?? [],
          waitingRooms: initialConfig.waitingRooms ?? [],
          role: initialConfig.role ?? StaffType.MEDECIN,
        })
        setActiveStep(STEP_KEYS.length - 1) // Go to review
        setSelectedAct(null)
      } else {
        setFormData({
          actId: '',
          establishmentId: '',
          role: StaffType.MEDECIN,
          autoCreateDesmos: true,
          autoCreateFSP: false,
          conditions: [],
          waitingRooms: [],
        })
        setActiveStep(0)
        setSelectedAct(null)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialConfig])

  // Resolve the act object once acts load (for edit mode)
  useEffect(() => {
    if (formData.actId && acts.length > 0 && !selectedAct) {
      const found = acts.find((a) => a._id === formData.actId)
      if (found) {
        setSelectedAct(found)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acts, formData.actId])

  const stepLabels = useMemo(() => [
    t('page.network.actWizard.step.establishment.label', { defaultValue: 'Establishment' }),
    t('page.network.actWizard.step.roleAct.label', { defaultValue: 'Role & Act' }),
    t('page.network.actWizard.step.automation.label', { defaultValue: 'Automation' }),
    t('page.network.actWizard.step.scope.label', { defaultValue: 'Scope' }),
    t('page.network.actWizard.step.conditions.label', { defaultValue: 'Conditions' }),
    t('page.network.actWizard.review.label', { defaultValue: 'Review' }),
  ], [t])

  const canProceed = (step: number): boolean => {
    switch (step) {
      case 0: return !!formData.establishmentId
      case 1: return !!formData.actId
      case 2: return true // automation toggles always valid
      case 3: return true // scope always valid (empty = all)
      case 4: return true // conditions always valid (empty = always)
      case 5: return true // review
      default: return false
    }
  }

  const handleNext = () => {
    if (activeStep < STEP_KEYS.length - 1) {
      setActiveStep((s) => s + 1)
    }
  }

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((s) => s - 1)
    }
  }

  const handleStepClick = (step: number) => {
    // Allow jumping to any completed step (or any step in edit mode)
    if (isEditMode || step <= activeStep) {
      setActiveStep(step)
    }
  }

  const handleSave = () => {
    onSave(formData)
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <StepEstablishment
            establishments={establishments}
            selected={formData.establishmentId}
            onSelect={(id) => {
              // Clear act when establishment changes (acts are establishment-scoped)
              if (id !== formData.establishmentId) {
                setFormData((prev) => ({ ...prev, establishmentId: id, actId: '' }))
                setSelectedAct(null)
              } else {
                setFormData((prev) => ({ ...prev, establishmentId: id }))
              }
            }}
          />
        )
      case 1:
        return (
          <StepRoleAndAct
            role={formData.role}
            onRoleChange={(role) => setFormData((prev) => ({ ...prev, role }))}
            selectedAct={selectedAct}
            onActChange={(act) => {
              setSelectedAct(act)
              setFormData((prev) => ({ ...prev, actId: act?._id ?? '' }))
            }}
            establishmentId={formData.establishmentId}
          />
        )
      case 2:
        return (
          <StepAutomation
            autoCreateDesmos={formData.autoCreateDesmos}
            autoCreateFSP={formData.autoCreateFSP}
            onDesmosChange={(v) => setFormData((prev) => ({ ...prev, autoCreateDesmos: v }))}
            onFSPChange={(v) => setFormData((prev) => ({ ...prev, autoCreateFSP: v }))}
          />
        )
      case 3:
        return (
          <StepScope
            waitingRooms={waitingRooms}
            selectedWaitingRooms={formData.waitingRooms}
            onWaitingRoomsChange={(ids) => setFormData((prev) => ({ ...prev, waitingRooms: ids }))}
          />
        )
      case 4:
        return (
          <StepConditions
            conditions={formData.conditions}
            onConditionsChange={(conditions) => setFormData((prev) => ({ ...prev, conditions }))}
          />
        )
      case 5:
        return (
          <StepReview
            formData={formData}
            establishments={establishments}
            waitingRooms={waitingRooms}
            actDetails={selectedAct}
          />
        )
      default:
        return null
    }
  }

  const isLastStep = activeStep === STEP_KEYS.length - 1

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode
          ? t('page.network.actWizard.editTitle', { defaultValue: 'Edit act configuration' })
          : t('page.network.actWizard.addTitle', { defaultValue: 'New act configuration' })}
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, mt: 1 }}>
          {stepLabels.map((label, index) => (
            <Step
              key={label}
              completed={index < activeStep}
              sx={{ cursor: (isEditMode || index <= activeStep) ? 'pointer' : 'default' }}
              onClick={() => handleStepClick(index)}
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 300 }}>
          {renderStepContent()}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="text">
          {t('common:button.cancel.label', { defaultValue: 'Cancel' })}
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} startIcon={<ArrowBackIcon />} variant="outlined">
            {t('common:button.back.label', { defaultValue: 'Back' })}
          </Button>
        )}
        {isLastStep ? (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!canProceed(activeStep) || isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {isSaving
              ? t('common:button.saving.label', { defaultValue: 'Saving...' })
              : t('common:button.save.label', { defaultValue: 'Save' })}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={!canProceed(activeStep)}
            endIcon={<ArrowForwardIcon />}
          >
            {t('common:button.next.label', { defaultValue: 'Next' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
